<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="title"
    size="large"
  >
    <template v-if="event" #header>
      <label class="flex items-center gap-2 font-medium">
        <span class="capitalize-first">
          {{ $t('global.subject_to_confirmation') }}
        </span>
        <BaseSwitch
          :key="event.subject_to_confirmation"
          name="subject_to_confirmation"
          @change="getInputValue"
          v-model="event.subject_to_confirmation"
        />
      </label>
    </template>
    <template #body>
      <div class="flex w-full flex-col gap-3 pt-1">
        <!--#region Work -->
        <div
          v-if="
            !['MAI', 'FOR', 'OD', 'SEV'].includes(event.type) &&
            (!event.subject_to_confirmation || event.id !== null)
          "
          class="grid-form items-end"
        >
          <BaseSelectSimpleAjax
            v-if="event.work || !event.id"
            :key="event"
            url="api/work-for-select"
            :label="$t('global.work')"
            v-model="event.work_id"
            name="work_id"
            trackBy="id"
            attributeLabel="title"
            :defaultAjax="computedWork"
          />
          <div v-if="event.work">
            <BaseButton
              icon="chevron-right"
              :title="$t('button.to_m', { var: $t('attributes.work') })"
              @click="
                $router.push({
                  name: 'work_form_edit_route',
                  params: { id: event.work_id }
                })
              "
            />
          </div>
        </div>
        <!--#endregion -->

        <!--#region subject to confirmation -->
        <div v-if="event.subject_to_confirmation" class="grid-form">
          <div class="col-span-full">
            <SelectCustomer
              name="customer_id"
              :key="event.customer_id"
              :errors="errors"
              :get-input-value="getInputValue"
              :model="event"
              :permission="!$can('manage_planning')"
              :required="true"
              :metadata="metadata"
            />
          </div>
          <BaseInput
            :label="$t('attributes.first_name')"
            :required="true"
            :placeholder="$t('attributes.first_name')"
            name="first_name"
            v-model="event.first_name"
            :errors="errors ? errors.first_name : []"
          />
          <BaseInput
            :label="$t('attributes.last_name')"
            :required="true"
            :placeholder="$t('attributes.last_name')"
            name="last_name"
            v-model="event.last_name"
            :errors="errors ? errors.last_name : []"
          />
        </div>
        <!--#endregion -->

        <formEvent
          :key="event"
          :errors="errors"
          :filtered-assets="filteredAssets"
          :get-input-value="getInputValue"
          :after-work-selected="afterWorkSelected"
          :index="index"
          :metadata="metadata"
        />

        <!--#region automatic deletion -->
        <template v-if="event.subject_to_confirmation && event.id">
          <h4 class="mt-3 font-bold capitalize-first">
            {{
              $t('validation.automatic_unconfirmed_deletion_m', {
                var: $t('attributes.work')
              })
            }}
          </h4>
          <div class="grid-form">
            <BaseDateInput
              :label="$t('attributes.date_from')"
              :placeholder="$t('attributes.date_from')"
              name="expiration_date"
              :value="event.expiration_date"
              :errors="errors.expiration_date"
              :disabled="false"
              :get-input-value="getInputValue"
            />
            <BaseTimeInput
              :label="$t('attributes.time')"
              placeholder="10:00"
              name="expiration_time"
              :value="event.expiration_time"
              :errors="errors.expiration_time"
              :event="event"
              :get-input-value="getInputValue"
              :index="index"
            />
          </div>
        </template>
        <!--#endregion -->
      </div>
    </template>
    <template #footer>
      <BaseButton
        :icon="submitButtonIcon"
        color="primary"
        :title="submitButtonTitle"
        @click="submitForm"
      />
      <BaseButton :title="$t('button.close')" color="dark" @click="close" />
      <span class="flex-1"></span>
      <BaseButton
        v-if="!event.work_id && event.id && $can('delete_planning')"
        icon="trash"
        color="danger"
        :title="$t('button.delete_v', { var: $t('global.event') })"
        @click="deleteEvent(event.id)"
      />
    </template>
  </BaseCenteredModal>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import moment from 'moment'

import FormEvent from '../../../components/Event/FormEvent'

export default {
  name: 'EventFormDialog',
  emits: ['close', 'backdropClicked', 'refetchCalendarEvents'],
  components: { BaseCenteredModal, FormEvent },
  props: {
    isModalVisible: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      editeditemList: []
    }
  },
  computed: {
    ...mapGetters({
      event: 'getEvent',
      metadata: 'getEventMetadata',
      errors: 'auth/getErrors'
    }),
    localIsModalOpen: {
      get() {
        return this.isModalVisible
      },
      set(value) {
        if (!value) this.close()
      }
    },
    filteredAssets() {
      let tempModels = this.metadata.assets
      if (tempModels !== undefined && this.event.type) {
        tempModels = tempModels.filter((asset) => {
          return this.metadata.assets_type[this.event.type].includes(asset.type)
        })
      }
      return tempModels
    },
    computedWork() {
      return this.event && this.event.work
        ? {
            id: this.event.work.id,
            title: `${this.event.work.title} ${this.event.work.reference ? `(${this.event.work.reference})` : ''}`
          }
        : {}
    },
    title() {
      return this.event.id ? this.event.title : this.$t('global.new_event')
    },
    submitButtonTitle() {
      return this.event.id
        ? this.$t('button.edit_new_mv', { var: this.$t('global.event') })
        : this.$t('button.add_new_m', { var: this.$t('global.event') })
    },
    submitButtonIcon() {
      return this.event.id ? 'edit' : 'plus'
    }
  },
  methods: {
    ...mapActions([
      'fetchEventById',
      'fetchNewEvent',
      'postEvent',
      'updateEvent',
      'deleteEventById',
      'undoDeleteEventById'
    ]),
    ...mapMutations(['setEvent', 'alterEvent', 'deleteEventKey']),
    async loadEvent(eventId, eventTime, resource_id) {
      if (eventId) {
        await this.fetchEventById({ event_id: eventId })
      } else {
        await this.fetchNewEvent()
        // A selection gives "start _ end"; a click only its start: end one
        // slot (30 minutes) later, or the same day for a whole day.
        const a_times = eventTime.split(' _ ')
        const allDay = a_times[0].length === 10
        const start = moment(a_times[0])
        const end = a_times[1]
          ? moment(a_times[1])
          : allDay
            ? start.clone()
            : start.clone().add(30, 'minutes')
        this.setEvent({
          ...this.event,
          date_from: start.format('DD/MM/YYYY'),
          date_to: end.format('DD/MM/YYYY'),
          asset_id: resource_id ? parseInt(resource_id.id) : null,
          all_day: allDay,
          time_from: allDay ? null : start.format('HH:mm'),
          time_to: allDay ? null : end.format('HH:mm')
        })
      }
    },
    deleteEvent(id) {
      this.deleteEventById({ id: id }).then(() => {
        this.$emit('refetchCalendarEvents')
        this.close()
        this.$h.toastAction(
          'delete',
          this.$t('toasts.deleted_m', { var: this.$t('global.event') }),
          null,
          [
            {
              title: this.$t('button.cancel'),
              class: 'text-red-700',
              action: () => this.undoDeleteEvent(id)
            }
          ]
        )
      })
    },
    undoDeleteEvent(id) {
      this.undoDeleteEventById({ id: id }).then(() => {
        this.$emit('refetchCalendarEvents')
        this.$h.toastSuccess(
          this.$t('toasts.reseted_m', { var: this.$t('global.event') })
        )
      })
    },
    getInputValue({ attribute, value }) {
      if (attribute === 'type') {
        this.deleteEventKey('asset_id')
      }
      this.alterEvent({ attribute, value })
      // this.event[attribute] = value
    },
    afterWorkSelected(work) {
      this.event.work_id = work.id
    },
    close() {
      this.$emit('close')
    },
    // The calendar is refetched whether the save succeeded or not.
    submitNewEvent() {
      return this.postEvent({ event: this.event })
        .then(this.close)
        .finally(() => this.$emit('refetchCalendarEvents'))
    },
    submitUpdateEvent() {
      return this.updateEvent({ id: this.eventId, event: this.event })
        .then(this.close)
        .finally(() => this.$emit('refetchCalendarEvents'))
    },
    submitForm() {
      if (this.event.id) {
        this.submitUpdateEvent()
      } else {
        this.submitNewEvent()
      }
    }
  },
  watch: {}
}
</script>
